import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowUpRight, CheckCircle2, Clock3, Loader2, Mail, MapPin, Phone } from "lucide-react";
import { MaskedLines, Reveal } from "@/components/Reveal";
import FAQ from "@/components/FAQ";
import Founder from "@/components/Founder";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DETAILS = [
  {
    icon: Phone,
    label: "Call us",
    value: "+1 (307) 500-7253",
    href: "tel:+13075007253",
    tid: "contact-page-phone-link",
  },
  {
    icon: Mail,
    label: "Email",
    value: "contact@viberagroup.com",
    href: "mailto:contact@viberagroup.com",
    tid: "contact-page-email-link",
  },
  {
    icon: MapPin,
    label: "Based in",
    value: "Wyoming, United States",
    href: null,
    tid: "contact-page-location",
  },
];

const BUDGETS = ["Under $10k / month", "$10k – $25k / month", "$25k – $100k / month", "$100k+ / month"];
const PLATFORMS = ["Meta Ads", "Google Ads", "Both"];

const FAQ_ITEMS = [
  {
    q: "What happens after I send this?",
    a: "You'll hear from us within one business day with availability for a 30-minute audit call. No pressure, no pitch deck theater.",
  },
  {
    q: "What does the strategic audit cover?",
    a: "Account structure, tracking integrity, creative pipeline and competitive position — plus a candid read on whether we're the right partner for your stage.",
  },
  {
    q: "What budgets do you work with?",
    a: "We work with businesses investing meaningfully in paid media, and we'll be candid if the maths of paid scale doesn't yet work at your level.",
  },
  {
    q: "Do you work with businesses outside the US?",
    a: "Yes — our systems are geography-agnostic, and English-language markets are our specialty.",
  },
];

const inputCls =
  "w-full rounded-xl border border-white/15 bg-surface-elevated px-4 py-3.5 text-sm text-white placeholder:text-gray-500 outline-none transition-colors focus:border-flame focus:ring-1 focus:ring-flame/60";

const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    budget: BUDGETS[0],
    platforms: ["Both"],
    message: "",
  });
  const [status, setStatus] = useState("idle");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const togglePlatform = (p) =>
    setForm((f) => ({
      ...f,
      platforms: f.platforms.includes(p) ? f.platforms.filter((x) => x !== p) : [...f.platforms, p],
    }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("success");
      toast.success("Message sent — we'll reply within one business day.");
    } catch (err) {
      setStatus("idle");
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Something went wrong. Please call us instead.");
    }
  };

  return (
    <>
      <section className="relative overflow-hidden pt-32 sm:pt-40 pb-16" data-testid="contact-hero">
        <div className="absolute inset-0 hero-glow" aria-hidden="true" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame mb-6">
            Contact — Vibera Group LLC
          </p>
          <h1 className="font-syne text-4xl sm:text-6xl lg:text-7xl font-extrabold tracking-tight leading-[1.05] text-white">
            <MaskedLines lines={[<>Initiate</>, <><span className="text-flame">Scale.</span></>]} />
          </h1>
          <Reveal delay={0.4}>
            <p className="mt-6 text-lg text-gray-300 leading-relaxed max-w-xl">
              Tell us where you are and where the revenue needs to be. We respond within one business day.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="pb-24" data-testid="contact-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-5 space-y-6">
            {DETAILS.map((d, i) => (
              <Reveal key={d.label} delay={0.08 * i}>
                <div className="rounded-2xl border border-white/10 bg-surface p-7 flex items-start gap-5">
                  <div className="h-11 w-11 shrink-0 rounded-xl bg-flame/10 border border-flame/30 flex items-center justify-center">
                    <d.icon className="h-5 w-5 text-flame" />
                  </div>
                  <div>
                    <p className="font-mono text-xs uppercase tracking-[0.2em] text-gray-500">{d.label}</p>
                    {d.href ? (
                      <a href={d.href} data-testid={d.tid} className="mt-2 block font-semibold text-white hover:text-flame transition-colors">
                        {d.value}
                      </a>
                    ) : (
                      <p className="mt-2 font-semibold text-white" data-testid={d.tid}>{d.value}</p>
                    )}
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3}>
              <div className="rounded-2xl border border-flame/30 bg-flame/5 p-7 flex items-start gap-5" data-testid="contact-sla-badge">
                <Clock3 className="h-5 w-5 text-flame shrink-0 mt-0.5" />
                <p className="text-sm text-gray-300 leading-relaxed">
                  <span className="font-semibold text-white">Response guarantee:</span> every inquiry gets a
                  human reply within one business day.
                </p>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.15} className="h-full">
              <div className="h-full rounded-3xl border border-white/10 bg-surface p-6 sm:p-10" data-testid="contact-form-card">
                {status === "success" ? (
                  <div className="h-full flex flex-col items-center justify-center text-center py-16" data-testid="contact-success-panel">
                    <CheckCircle2 className="h-14 w-14 text-flame" />
                    <h2 className="mt-6 font-syne text-2xl sm:text-3xl font-bold text-white">Message received.</h2>
                    <p className="mt-3 text-sm sm:text-base text-gray-400 max-w-sm">
                      Thank you — a strategist will reply to {form.email} within one business day.
                    </p>
                    <button
                      onClick={() => { setStatus("idle"); setForm({ ...form, message: "" }); }}
                      data-testid="contact-send-another-button"
                      className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 px-6 py-3 text-sm font-semibold text-white transition-colors hover:border-flame hover:text-flame"
                    >
                      Send another message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-5" data-testid="contact-form">
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="name" className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Full name *</label>
                        <input id="name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Jordan Smith" className={inputCls} data-testid="contact-form-name-input" />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Work email *</label>
                        <input id="email" required type="email" value={form.email} onChange={set("email")} placeholder="jordan@company.com" className={inputCls} data-testid="contact-form-email-input" />
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div>
                        <label htmlFor="company" className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Company</label>
                        <input id="company" value={form.company} onChange={set("company")} placeholder="Company LLC" className={inputCls} data-testid="contact-form-company-input" />
                      </div>
                      <div>
                        <label htmlFor="budget" className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Monthly ad budget</label>
                        <select id="budget" value={form.budget} onChange={set("budget")} className={`${inputCls} appearance-none cursor-pointer`} data-testid="contact-form-budget-select">
                          {BUDGETS.map((b) => <option key={b} value={b} className="bg-surface-elevated">{b}</option>)}
                        </select>
                      </div>
                    </div>
                    <div>
                      <span className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Platforms of interest</span>
                      <div className="flex flex-wrap gap-3">
                        {PLATFORMS.map((p) => (
                          <label
                            key={p}
                            className={`cursor-pointer rounded-full border px-5 py-2.5 text-sm font-medium transition-colors ${
                              form.platforms.includes(p)
                                ? "border-flame bg-flame/10 text-white"
                                : "border-white/15 text-gray-400 hover:border-white/35 hover:text-gray-200"
                            }`}
                          >
                            <input
                              type="checkbox"
                              className="sr-only"
                              checked={form.platforms.includes(p)}
                              onChange={() => togglePlatform(p)}
                              data-testid={`contact-form-platform-${p.toLowerCase().replace(/\s+/g, "-")}`}
                            />
                            {p}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-xs font-semibold uppercase tracking-wider text-gray-400 mb-2">Project goals *</label>
                      <textarea id="message" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder="What are you selling, who are you selling to, and what does success look like in 90 days?" className={`${inputCls} resize-none`} data-testid="contact-form-message-input" />
                    </div>
                    <motion.button
                      whileTap={{ scale: 0.98 }}
                      type="submit"
                      disabled={status === "sending"}
                      data-testid="contact-form-submit-button"
                      className="w-full sm:w-auto inline-flex justify-center items-center gap-2 rounded-full bg-flame px-10 py-4 font-semibold text-white transition-all hover:bg-flame-hover disabled:opacity-60"
                    >
                      {status === "sending" ? (
                        <><Loader2 className="h-5 w-5 animate-spin" />Sending…</>
                      ) : (
                        <>Send message<ArrowUpRight className="h-5 w-5" /></>
                      )}
                    </motion.button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <Founder compact />

      <section className="pb-24 border-t border-white/10 pt-20" data-testid="contact-faq">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-flame">Good to know</p>
            <h2 className="mt-4 font-syne text-3xl sm:text-4xl font-bold tracking-tight text-white mb-10">
              New client onboarding.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <FAQ items={FAQ_ITEMS} testPrefix="contact-faq" />
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default Contact;
